import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  GitBranch, 
  Mail, 
  Lock, 
  User, 
  ArrowRight,
  Users,
  Lightbulb,
  TrendingUp,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const features = [
  {
    icon: GitBranch,
    title: 'Collaborative Mindmaps',
    description: 'Build knowledge trees together with the community'
  },
  {
    icon: Users,
    title: 'Crowd-sourced Wisdom',
    description: 'Tap into collective intelligence from experts worldwide'
  },
  {
    icon: Lightbulb,
    title: 'Visual Learning',
    description: 'Understand complex topics through intuitive mindmaps'
  },
  {
    icon: TrendingUp,
    title: 'Always Growing',
    description: 'Knowledge that evolves and expands with every contribution'
  }
];

export default function AuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (!formData.name.trim()) {
        toast.error('Please enter your name');
        return;
      }
    }

    setLoading(true);

    try {
      if (isLogin) {
        const response = await authAPI.login({
          email: formData.email,
          password: formData.password,
        });
        login(response.data.user, response.data.token);
        toast.success('Welcome back!');
      } else {
        const response = await authAPI.register({
          name: formData.name.trim(),
          email: formData.email,
          password: formData.password,
        });
        login(response.data.user, response.data.token);
        toast.success('Welcome to Clarities!');
      }
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || (isLogin ? 'Invalid credentials' : 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left Side - Product Info */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 border border-white/20 rounded-full"></div>
          <div className="absolute bottom-40 right-10 w-96 h-96 border border-white/20 rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white/20 rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-16">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <GitBranch className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold">Clarities</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Knowledge grows when<br />
            <span className="text-orange-400">we build together</span>
          </h1>
          
          <p className="text-lg text-slate-300 mb-12 max-w-md">
            Join thousands of curious minds creating collaborative mindmaps. 
            Ask questions, contribute answers, and explore the world&apos;s knowledge visually.
          </p>

          {/* Features */}
          <div className="space-y-6">
            {features.map((feature) => (
              <div key={feature.title} className="flex items-start gap-4 group">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-orange-500/20 transition-colors">
                  <feature.icon className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-slate-400">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="relative z-10 flex gap-12 pt-8 border-t border-white/10">
          <div>
            <p className="text-3xl font-bold text-orange-400">10K+</p>
            <p className="text-sm text-slate-400">Active Users</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-orange-400">50K+</p>
            <p className="text-sm text-slate-400">Mindmaps Created</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-orange-400">200K+</p>
            <p className="text-sm text-slate-400">Knowledge Nodes</p>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <GitBranch className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900">Clarities</span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                {isLogin ? 'Welcome back' : 'Create your account'}
              </h2>
              <p className="text-slate-500">
                {isLogin 
                  ? 'Sign in to continue your journey' 
                  : 'Start contributing to the knowledge community'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-slate-700">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required={!isLogin}
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10 h-11"
                      placeholder="John Doe"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="you@example.com"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 h-11"
                    placeholder="••••••••"
                    data-testid="password-input"
                  />
                </div>
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required={!isLogin}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10 h-11"
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white mt-6"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <div className="spinner w-5 h-5"></div>
                ) : (
                  <>
                    {isLogin ? 'Sign in' : 'Create Account'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Switch Mode */}
            <p className="mt-6 text-center text-sm text-slate-500">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button 
                onClick={switchMode}
                className="text-orange-500 hover:text-orange-600 font-medium"
                data-testid="switch-mode-btn"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>Free to use</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>No credit card</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
